import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Pencil, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { title: "", tagline: "", summary: "", head_lawyer_id: null, order: 0, seo_title: "", seo_description: "" };

export default function AdminDepartments() {
  const { headers } = useAuth();
  const [items, setItems] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [d, l] = await Promise.all([
        api.get("/admin/departments", { headers }),
        api.get("/admin/lawyers", { headers }),
      ]);
      setItems(d.data);
      setLawyers(l.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing.id) await api.put(`/admin/departments/${editing.id}`, editing, { headers });
      else await api.post(`/admin/departments`, editing, { headers });
      toast.success("Saved.");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this department? Services and lawyers will be unlinked.")) return;
    try {
      await api.delete(`/admin/departments/${id}`, { headers });
      toast.success("Deleted.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-departments">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="overline text-graphite/55">Departments</p>
          <h1 className="mt-3 h-display text-4xl md:text-5xl">Manage departments</h1>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} className="cec-btn cec-btn-primary" data-testid="admin-add-department">
          <Plus size={16} /> Add department
        </button>
      </div>

      <div className="hairline-top">
        {items.map((d) => {
          const head = lawyers.find((l) => l.id === d.head_lawyer_id);
          return (
            <div key={d.id} className="grid grid-cols-12 gap-4 py-5 hairline-bottom items-center" data-testid={`admin-dept-${d.id}`}>
              <div className="col-span-4">
                <p className="font-semibold">{d.title}</p>
                <p className="text-sm text-graphite/60">{d.tagline}</p>
              </div>
              <div className="col-span-4 text-sm">{head ? `Head: ${head.name}` : <span className="text-graphite/45">No head assigned</span>}</div>
              <div className="col-span-2 text-sm text-graphite/60">Order {d.order}</div>
              <div className="col-span-2 flex justify-end gap-2">
                <button className="p-2 hover:text-[#1E3668]" onClick={() => setEditing({ ...d })} aria-label="Edit" data-testid={`admin-edit-dept-${d.id}`}><Pencil size={16} /></button>
                <button className="p-2 hover:text-red-700" onClick={() => del(d.id)} aria-label="Delete"><Trash2 size={16} /></button>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-graphite/40 flex items-center justify-center px-4 overflow-y-auto py-12" onClick={() => setEditing(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-warmwhite max-w-3xl w-full p-8 md:p-12 hairline" data-testid="admin-dept-form">
            <div className="flex items-start justify-between">
              <div>
                <p className="overline text-graphite/55">{editing.id ? "Edit" : "New"} department</p>
                <h2 className="mt-3 h-display text-3xl">{editing.title || "Untitled"}</h2>
              </div>
              <button type="button" onClick={() => setEditing(null)} className="p-2"><X size={18} /></button>
            </div>
            <div className="space-y-2 mt-8">
              <div><label className="overline text-graphite/55">Title</label><input className="edit-input" required value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
              <div><label className="overline text-graphite/55">Tagline</label><input className="edit-input" value={editing.tagline || ""} onChange={(e) => setEditing({ ...editing, tagline: e.target.value })} /></div>
              <div><label className="overline text-graphite/55">Summary</label><textarea className="edit-textarea" rows={3} value={editing.summary || ""} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <label className="overline text-graphite/55">Head of Department</label>
                  <select className="edit-input" value={editing.head_lawyer_id || ""} onChange={(e) => setEditing({ ...editing, head_lawyer_id: e.target.value || null })} data-testid="admin-dept-head">
                    <option value="">— None —</option>
                    {lawyers.map((l) => <option key={l.id} value={l.id}>{l.name} {l.title ? `· ${l.title}` : ""}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline text-graphite/55">Display order</label>
                  <input type="number" className="edit-input" value={editing.order} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) || 0 })} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6 pt-6 hairline-top">
                <div><label className="overline text-graphite/55">SEO title</label><input className="edit-input" value={editing.seo_title || ""} onChange={(e) => setEditing({ ...editing, seo_title: e.target.value })} /></div>
                <div><label className="overline text-graphite/55">SEO description</label><input className="edit-input" value={editing.seo_description || ""} onChange={(e) => setEditing({ ...editing, seo_description: e.target.value })} /></div>
              </div>
            </div>
            <div className="mt-10 flex items-center gap-3">
              <button type="submit" disabled={busy} className="cec-btn cec-btn-primary" data-testid="admin-dept-save">{busy ? "Saving…" : "Save"}</button>
              <button type="button" onClick={() => setEditing(null)} className="cec-btn">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
